import type { JournalListRow } from "../../journal/journal-list"
import { buildDayList } from "../../journal/journal-list"
import type { JournalColumn, JournalDayModel } from "../../journal/journal-model"
import { formatDuration, formatRange } from "../../journal/journal-time"
import { formatDateLong, formatTimeOfDay } from "../../lib/format"

/**
 * «Мой день» мастера (5-М1) как данные, отдельно от разметки.
 *
 * ЭКРАН ОТВЕЧАЕТ НА ОДИН ВОПРОС: кто у меня следующий и когда я свободен (ТЗ §5.3).
 * Мастер открывает его между двумя клиентами, мокрыми руками, на 20–40 секунд. Всё,
 * что считается здесь, считается ради этого вопроса, поэтому расчёт вынесен из разметки
 * и проверяется тестом: «следующий» и строка смены — это то, что ломается молча
 * и замечается не на приёмке, а в зале.
 *
 * ПОЧЕМУ ЛЕНТА СТРОИТСЯ ЧУЖОЙ ФУНКЦИЕЙ. buildDayList (journal/journal-list.ts) уже
 * раскладывает колонку в ленту с промежутками и помечает ближайшую запись. Второй такой
 * расчёт для мастера означал бы два ответа на вопрос «кто следующий» в одном кабинете.
 */

/** Чем сегодня кончился день мастера. От этого зависит и текст, и наличие «Записать». */
export type MasterDayKind =
  /** Смена есть: лента записей и промежутков. */
  | "shift"
  /** Смена есть, записей нет. Не ошибка и не грустная картинка (ТЗ §5.3). */
  | "empty"
  /** Сегодня выходной, но смены заведены — известна следующая. */
  | "dayoff"
  /** Графика нет вовсе: первый день салона. Кнопок в чужие настройки здесь не бывает. */
  | "no-shifts"

export interface MasterDayEmpty {
  readonly title: string
  readonly text: string
}

export interface MasterDay {
  readonly kind: MasterDayKind
  /** Строка под заголовком: смена, число записей, свободное время и возраст данных. */
  readonly shiftLine: string
  readonly rows: readonly JournalListRow[]
  /** Ближайшая запись — та, у которой в ленте стоит чип «Следующий». */
  readonly nextCardId: string | null
  /** Пустое состояние вместо ленты. null — лента есть. */
  readonly empty: MasterDayEmpty | null
  /** Сколько услуг мастер сделал за смену. Деньги к ним приписывает экран, если они видны. */
  readonly doneServices: number
}

/** Следующая смена — для единственной строки состояния «выходной». */
export interface NextShift {
  readonly date: string
  readonly startMinute: number
  readonly endMinute: number
}

function shiftText(column: JournalColumn): string | null {
  const start = column.workStartMinute
  const end = column.workEndMinute
  if (start === null || end === null) return null
  return `Смена ${formatRange(start, end)}`
}

/**
 * Свободное время смены. Считается по ленте, а не по сводке дня: сводка журнала
 * складывает свободное время ВСЕХ мастеров, а мастеру нужно своё — он по этому числу
 * решает, успеет ли пообедать (ТЗ §2.6).
 */
function freeMinutesOf(rows: readonly JournalListRow[]): number {
  return rows
    .filter((row) => row.kind === "gap")
    .reduce((sum, row) => sum + (row.endMinute - row.startMinute), 0)
}

/**
 * День мастера.
 *
 * @param nextShift ближайшая смена после сегодняшней. null означает «не знаем» —
 *   и тогда день без смены считается ненастроенным графиком, а не выходным. Разница
 *   не косметическая: выходному полагается строка «следующая смена — четверг», а
 *   ненастроенному графику — объяснение, что смены ставит администратор.
 */
export function buildMasterDay(input: {
  readonly model: JournalDayModel
  readonly employeeId: string | null
  readonly nowMinute: number
  readonly nextShift: NextShift | null
}): MasterDay {
  const { model } = input
  const column =
    model.columns.find((item) => item.employee.id === input.employeeId) ?? model.columns[0] ?? null

  const dataAt = `данные на ${formatTimeOfDay(model.generatedAt, model.timeZone)}`

  if (column === null || column.workStartMinute === null || column.workEndMinute === null) {
    // Смены сегодня нет. Что именно показать — решает знание о следующей смене.
    const knownNext = input.nextShift
    if (knownNext === null) {
      return {
        kind: "no-shifts",
        shiftLine: "Смены не заведены",
        rows: [],
        nextCardId: null,
        empty: {
          title: "График смен ещё не заведён",
          text: "Его ставит администратор. Как только смены появятся, здесь будет ваш день.",
        },
        doneServices: 0,
      }
    }
    return {
      kind: "dayoff",
      shiftLine: "Выходной",
      rows: [],
      nextCardId: null,
      empty: {
        title: "Сегодня у вас выходной",
        text: `Следующая смена — ${formatDateLong(knownNext.date, model.timeZone)}, ${formatRange(knownNext.startMinute, knownNext.endMinute)}.`,
      },
      doneServices: 0,
    }
  }

  const rows = buildDayList(column, input.nowMinute)
  const nextRow = rows.find((row) => row.kind === "appointment" && row.isNext)
  const nextCardId = nextRow !== undefined && nextRow.kind === "appointment" ? nextRow.card.id : null

  const live = column.cards.filter((card) => card.status !== "cancelled")
  const free = freeMinutesOf(rows)
  const shift = shiftText(column) ?? "Смена"

  // Завершённые визиты — это и есть «мои работы за смену»: услуги считаются по ним,
  // а не по всем записям дня, иначе итог смены вырастет раньше, чем мастер её отработал.
  const done = column.cards.filter((card) => card.status === "completed")
  const doneServices = done.reduce((sum, card) => sum + 1 + card.extraServices, 0)

  if (live.length === 0) {
    return {
      kind: "empty",
      shiftLine: `${shift} · записей нет · свободно ${formatDuration(free)}`,
      rows,
      nextCardId: null,
      empty: {
        title: "Сегодня записей нет",
        text: `Смена с ${formatRange(column.workStartMinute, column.workEndMinute).replace("–", " до ")}. Клиенты могут записаться онлайн — свободно ${formatDuration(free)}.`,
      },
      doneServices,
    }
  }

  return {
    kind: "shift",
    // Возраст данных стоит в строке ВСЕГДА, а не только когда он велик: подпись времени —
    // единственное, что отделяет рабочий экран от вранья (ТЗ §2.11.4).
    shiftLine: `${shift} · записей: ${String(live.length)} · свободно ${formatDuration(free)} · ${dataAt}`,
    rows,
    nextCardId,
    empty: null,
    doneServices,
  }
}
